import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { FiX, FiUpload, FiCheck, FiMapPin, FiDollarSign, FiFileText, FiShield, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useTokenization } from '../../contexts/TokenizationContext';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useKYC } from '../../contexts/KYCContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import {
  mapCategoryToSector,
  normalizeSectorType,
  resolveAuthorizedTokenizerIdForSector,
  formatSectorLabel,
  allowedSectorEnumsFromIssuerProfile,
  isUiCategoryAllowedForTokenizer,
  isAssetClassSectorAllowedForTokenizer
} from '../../api/smartTokenizerMappers';
import toast from 'react-hot-toast';

const WizardContainer = styled.div`
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const WizardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const WizardTitle = styled.h2`
  font-size: 1.8rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #a0a0a0;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ProgressBar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
`;

const ProgressStep = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .step-number {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.9rem;
    background: ${props => props.active ? '#4facfe' : props.completed ? '#00ff88' : 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.active || props.completed ? '#fff' : '#a0a0a0'};
    border: 2px solid ${props => props.active ? '#4facfe' : props.completed ? '#00ff88' : 'rgba(255, 255, 255, 0.1)'};
  }
  
  .step-label {
    color: ${props => props.active ? '#4facfe' : props.completed ? '#00ff88' : '#a0a0a0'};
    font-weight: ${props => props.active ? '600' : '400'};
  }
  
  .step-line {
    width: 3rem;
    height: 2px;
    background: ${props => props.completed ? '#00ff88' : 'rgba(255, 255, 255, 0.1)'};
    margin: 0 0.5rem;
  }
`;

const StepContent = styled(motion.div)`
  min-height: 400px;
`;

const CategorySelection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const CategoryOption = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid
    ${(props) =>
      props.$disabled ? 'rgba(255, 255, 255, 0.06)' : props.selected ? props.color : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 1.5rem;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.38 : 1)};
  pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};
  filter: ${(props) => (props.$disabled ? 'grayscale(0.65)' : 'none')};
  transition: all 0.3s ease;
  position: relative;

  ${(props) =>
    props.$disabled &&
    `
    &::after {
      content: 'Not authorized';
      position: absolute;
      top: 0.65rem;
      right: 0.65rem;
      font-size: 0.65rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: rgba(255, 180, 180, 0.85);
      border: 1px solid rgba(255, 120, 120, 0.35);
      border-radius: 6px;
      padding: 0.2rem 0.45rem;
      background: rgba(80, 20, 20, 0.35);
      pointer-events: none;
    }
  `}

  &:hover {
    border-color: ${(props) => (!props.$disabled ? props.color : 'rgba(255, 255, 255, 0.06)')};
    transform: ${(props) => (!props.$disabled ? 'translateY(-2px)' : 'none')};
  }

  .header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .icon {
    font-size: 2rem;
  }

  .title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #fff;
  }

  .description {
    color: #a0a0a0;
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const FormSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;

  .section-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    font-weight: 500;
    color: #fff;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }

  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: #4facfe;
      box-shadow: 0 0 0 2px rgba(79, 172, 254, 0.1);
    }

    &::placeholder {
      color: #a0a0a0;
    }
  }

  select {
    option {
      background: #1a1a3e;
      color: #fff;
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }

  .error {
    color: #ff4757;
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.02);

  &:hover {
    border-color: #4facfe;
    background: rgba(79, 172, 254, 0.05);
  }

  .upload-icon {
    font-size: 3rem;
    color: #4facfe;
    margin-bottom: 1rem;
  }

  .upload-text {
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .upload-subtext {
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const UploadedFiles = styled.div`
  margin-top: 1rem;

  .file-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background: rgba(79, 172, 254, 0.1);
    border: 1px solid rgba(79, 172, 254, 0.3);
    border-radius: 8px;
    margin-bottom: 0.5rem;

    .file-icon {
      color: #4facfe;
    }

    .file-name {
      flex: 1;
      color: #fff;
      font-size: 0.9rem;
    }

    .file-size {
      color: #a0a0a0;
      font-size: 0.8rem;
    }
  }
`;

const ReadinessNotice = styled.div`
  margin: 0 0 1.25rem;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  border: 1px solid rgba(251, 191, 36, 0.35);
  background: rgba(251, 191, 36, 0.08);
  color: #fde68a;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const WizardActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.primary {
    background: #4facfe;
    color: #fff;

    &:hover {
      background: #3d8bfe;
      transform: translateY(-1px);
    }

    &:disabled {
      background: #2a2a3e;
      color: #a0a0a0;
      cursor: not-allowed;
      transform: none;
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

const steps = [
  { id: 1, label: 'Category' },
  { id: 2, label: 'Basic Info' },
  { id: 3, label: 'Metadata' },
  { id: 4, label: 'Documents' },
  { id: 5, label: 'Review' }
];

function AssetCreationWizard({ onClose, selectedCategory }) {
  const { categories, createAsset, loading } = useTokenization();
  const { assetClasses, validateMetadataSchema } = useAssetClass();
  const { kycStatus } = useKYC();
  const { issuers, currentIssuer, isAdmin, isAuthorizedTokenizer } = useTokenIssuer();

  const sectorGateActive = isAuthorizedTokenizer && !isAdmin;
  const allowedSectorEnums = useMemo(() => {
    if (!sectorGateActive) return null;
    return allowedSectorEnumsFromIssuerProfile(currentIssuer);
  }, [sectorGateActive, currentIssuer]);

  const selectedCategoryAllowed =
    !selectedCategory?.id ||
    !sectorGateActive ||
    isUiCategoryAllowedForTokenizer(selectedCategory.id, allowedSectorEnums);

  const [currentStep, setCurrentStep] = useState(selectedCategory && selectedCategoryAllowed ? 2 : 1);
  const [useAssetClassMode, setUseAssetClassMode] = useState(false);
  const [formData, setFormData] = useState({
    category: selectedCategoryAllowed ? selectedCategory?.id || '' : '',
    assetClassId: '',
    name: '',
    description: '',
    valuationUSD: '',
    ownerAddress: '',
    custodian: '',
    location: '',
    country: '',
    metadata: {},
    documents: []
  });
  const [errors, setErrors] = useState({});

  const effectiveSector = useMemo(() => {
    if (useAssetClassMode && formData.assetClassId) {
      const ac = assetClasses.find((a) => a.id === formData.assetClassId);
      return ac?.sector || null;
    }
    if (formData.category) {
      return mapCategoryToSector(formData.category);
    }
    return null;
  }, [useAssetClassMode, formData.assetClassId, formData.category, assetClasses]);

  const tokenizerCoversSector = useMemo(() => {
    if (!effectiveSector) {
      return true;
    }
    if (sectorGateActive && allowedSectorEnums && allowedSectorEnums.size > 0) {
      const sec = normalizeSectorType(effectiveSector);
      if (sec && allowedSectorEnums.has(sec)) {
        return Boolean(currentIssuer?.tokenizerId);
      }
    }
    return Boolean(resolveAuthorizedTokenizerIdForSector(effectiveSector, issuers, currentIssuer));
  }, [effectiveSector, issuers, currentIssuer, sectorGateActive, allowedSectorEnums]);

  // Get current category or asset class
  const currentCategory = formData.category ? 
    Object.values(categories).find(cat => cat.id === formData.category) : null;
  
  const currentAssetClass = formData.assetClassId ? 
    assetClasses.find(ac => ac.id === formData.assetClassId) : null;

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!useAssetClassMode && !formData.category) {
          newErrors.category = 'Please select an asset category';
        }
        if (useAssetClassMode && !formData.assetClassId) {
          newErrors.assetClassId = 'Please select an asset class';
        }
        if (sectorGateActive && allowedSectorEnums) {
          if (!useAssetClassMode && formData.category && !isUiCategoryAllowedForTokenizer(formData.category, allowedSectorEnums)) {
            newErrors.category = 'This sector is not authorized for your tokenizer.';
          }
          if (useAssetClassMode && formData.assetClassId) {
            const ac = assetClasses.find((a) => a.id === formData.assetClassId);
            if (ac && !isAssetClassSectorAllowedForTokenizer(ac, allowedSectorEnums)) {
              newErrors.assetClassId = 'This asset class is outside your authorized sectors.';
            }
          }
        }
        break;
      case 2:
        if (!formData.name) newErrors.name = 'Asset name is required';
        if (!formData.description) newErrors.description = 'Description is required';
        if (!formData.valuationUSD || formData.valuationUSD <= 0) newErrors.valuationUSD = 'Valid valuation is required';
        break;
      case 3:
        if (useAssetClassMode && currentAssetClass) {
          const { messages, fieldErrors } = validateMetadataSchema(
            formData.metadata,
            currentAssetClass.standardSchema
          );
          if (messages.length > 0) {
            Object.entries(fieldErrors).forEach(([key, msg]) => {
              newErrors[`metadata.${key}`] = msg;
            });
          }
        } else if (currentCategory) {
          // Use traditional category validation
          currentCategory.metadataFields.forEach(field => {
            if (!formData.metadata[field]) {
              newErrors[`metadata.${field}`] = `${field} is required`;
            }
          });
        }
        break;
      case 4:
        if (formData.documents.length === 0) {
          newErrors.documents = 'At least one document is required';
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleInputChange = (field, value, isMetadata = false) => {
    if (isMetadata) {
      setFormData(prev => ({
        ...prev,
        metadata: { ...prev.metadata, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear error when user starts typing (asset-class schema uses metadata.<fieldKey>)
    if (isMetadata ? errors[`metadata.${field}`] : errors[field] || errors[`metadata.${field}`]) {
      setErrors((prev) => {
        const next = { ...prev };
        if (isMetadata) {
          delete next[`metadata.${field}`];
        } else {
          delete next[field];
          delete next[`metadata.${field}`];
        }
        return next;
      });
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newDocuments = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file
    }));
    
    setFormData(prev => ({
      ...prev,
      documents: [...prev.documents, ...newDocuments]
    }));
  };

  const handleSubmit = async () => {
    for (let s = 1; s <= 4; s += 1) {
      if (!validateStep(s)) {
        setCurrentStep(s);
        toast.error('Complete all required steps before submitting.');
        return;
      }
    }

    if (effectiveSector && !tokenizerCoversSector) {
      toast.error(
        `No approved authorized tokenizer covers ${formatSectorLabel(effectiveSector)}. Approve a tokenizer with this sector or switch account.`
      );
      setCurrentStep(1);
      return;
    }

    try {
      const assetData = {
        ...formData,
        isAssetClassMode: useAssetClassMode,
        assetClassInfo:
          useAssetClassMode && currentAssetClass
            ? {
                id: currentAssetClass.id,
                name: currentAssetClass.name,
                marketSymbol: currentAssetClass.marketSymbol,
                tradingUnit: currentAssetClass.tradingUnit,
                contractAddress: currentAssetClass.contractAddress
              }
            : null
      };

      await createAsset(assetData);
      onClose();
    } catch {
      /* Errors and partial success are handled in createAsset (toasts + state). */
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h3 style={{ color: '#fff', marginBottom: '1.5rem' }}>Choose Asset Type</h3>
            {sectorGateActive && (
              <p style={{ color: '#a0a0a0', fontSize: '0.9rem', marginBottom: '1rem', lineHeight: 1.5 }}>
                Only sectors assigned to your authorized tokenizer are enabled. All other categories are disabled.
                {allowedSectorEnums && allowedSectorEnums.size === 0 && (
                  <strong style={{ color: '#ffb4b4', display: 'block', marginTop: '0.5rem' }}>
                    No sectors are assigned to your account yet. Ask an administrator to approve your tokenizer and
                    assign sectors.
                  </strong>
                )}
              </p>
            )}

            {/* Mode Selection */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                marginBottom: '1rem',
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '0.5rem',
                borderRadius: '8px'
              }}>
                <button
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: !useAssetClassMode ? 'linear-gradient(135deg, #4facfe, #00f2fe)' : 'transparent',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => {
                    setUseAssetClassMode(false);
                    setFormData(prev => ({ ...prev, assetClassId: '', category: '' }));
                  }}
                >
                  Traditional Categories
                </button>
                <button
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: useAssetClassMode ? 'linear-gradient(135deg, #4facfe, #00f2fe)' : 'transparent',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => {
                    setUseAssetClassMode(true);
                    setFormData(prev => ({ ...prev, category: '', assetClassId: '' }));
                  }}
                >
                  Asset Classes ({assetClasses.length})
                </button>
              </div>
              <p style={{ color: '#a0a0a0', fontSize: '0.9rem', textAlign: 'center' }}>
                {useAssetClassMode 
                  ? 'Use predefined asset classes with standardized schemas for batch minting and spot trading'
                  : 'Use traditional asset categories with flexible metadata fields'
                }
              </p>
            </div>

            {/* Asset Class Selection */}
            {useAssetClassMode ? (
              <div>
                {assetClasses.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '3rem 2rem',
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: '1px dashed rgba(79, 172, 254, 0.3)',
                    borderRadius: '12px',
                    color: '#a0a0a0'
                  }}>
                    <FiShield style={{ fontSize: '3rem', color: '#4facfe', marginBottom: '1rem' }} />
                    <h4 style={{ color: '#fff', marginBottom: '0.5rem' }}>No Asset Classes Found</h4>
                    <p>Create asset classes first to enable professional batch minting and spot trading</p>
                  </div>
                ) : (
                  <CategorySelection>
                    {assetClasses.map((assetClass) => {
                      const acAllowed = isAssetClassSectorAllowedForTokenizer(assetClass, allowedSectorEnums);
                      return (
                      <CategoryOption
                        key={assetClass.id}
                        color="#4facfe"
                        $disabled={!acAllowed}
                        selected={formData.assetClassId === assetClass.id}
                        role="button"
                        tabIndex={acAllowed ? 0 : -1}
                        aria-disabled={!acAllowed}
                        onClick={() => {
                          if (!acAllowed) return;
                          handleInputChange('assetClassId', assetClass.id);
                        }}
                      >
                        <div className="header">
                          <span className="icon">🏭</span>
                          <span className="title">{assetClass.name}</span>
                        </div>
                        <div className="description">{assetClass.marketSymbol} • {assetClass.tradingUnit}</div>
                        <div style={{ 
                          marginTop: '0.5rem', 
                          fontSize: '0.8rem', 
                          color: '#4facfe',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.25rem'
                        }}>
                          {assetClass.standardSchema?.required?.slice(0, 3).map(field => (
                            <span key={field} style={{
                              background: 'rgba(79, 172, 254, 0.1)',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              border: '1px solid rgba(79, 172, 254, 0.3)'
                            }}>
                              {field}
                            </span>
                          ))}
                          {assetClass.standardSchema?.required?.length > 3 && (
                            <span style={{ color: '#a0a0a0' }}>
                              +{(assetClass.standardSchema?.required?.length ?? 0) - 3} more
                            </span>
                          )}
                        </div>
                      </CategoryOption>
                    );
                    })}
                  </CategorySelection>
                )}
                {errors.assetClassId && <div style={{ color: '#ff4757' }}>{errors.assetClassId}</div>}
              </div>
            ) : (
              /* Traditional Category Selection */
              <div>
                <CategorySelection>
                  {Object.values(categories).map((category) => {
                    const catAllowed = isUiCategoryAllowedForTokenizer(category.id, allowedSectorEnums);
                    return (
                    <CategoryOption
                      key={category.id}
                      color={category.color}
                      $disabled={!catAllowed}
                      selected={formData.category === category.id}
                      role="button"
                      tabIndex={catAllowed ? 0 : -1}
                      aria-disabled={!catAllowed}
                      onClick={() => {
                        if (!catAllowed) return;
                        handleInputChange('category', category.id);
                      }}
                    >
                      <div className="header">
                        <span className="icon">{category.icon}</span>
                        <span className="title">{category.name}</span>
                      </div>
                      <div className="description">{category.description}</div>
                    </CategoryOption>
                    );
                  })}
                </CategorySelection>
                {errors.category && <div style={{ color: '#ff4757' }}>{errors.category}</div>}
              </div>
            )}
          </StepContent>
        );

      case 2:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h3 style={{ color: '#fff', marginBottom: '1.5rem' }}>Basic Asset Information</h3>
            <FormGrid>
              <FormSection>
                <div className="section-title">
                  <FiFileText />
                  Asset Details
                </div>
                <FormGroup>
                  <label>Asset Name *</label>
                  <input
                    type="text"
                    placeholder="Enter asset name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                  />
                  {errors.name && <div className="error">{errors.name}</div>}
                </FormGroup>
                <FormGroup>
                  <label>Description *</label>
                  <textarea
                    placeholder="Describe the asset in detail"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                  />
                  {errors.description && <div className="error">{errors.description}</div>}
                </FormGroup>
              </FormSection>

              <FormSection>
                <div className="section-title">
                  <FiDollarSign />
                  Valuation & Ownership
                </div>
                <FormGroup>
                  <label>Current Valuation (USD) *</label>
                  <input
                    type="number"
                    placeholder="Enter current market value"
                    value={formData.valuationUSD}
                    onChange={(e) => handleInputChange('valuationUSD', Number(e.target.value))}
                  />
                  {errors.valuationUSD && <div className="error">{errors.valuationUSD}</div>}
                </FormGroup>
                <FormGroup>
                  <label>Owner identifier (on-chain or legal ID)</label>
                  <input
                    type="text"
                    placeholder="Wallet 0x… or legal / registry identifier"
                    value={formData.ownerAddress}
                    onChange={(e) => handleInputChange('ownerAddress', e.target.value)}
                  />
                  <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.35rem' }}>
                    Maps to the asset&apos;s <strong>current owner identifier</strong> in the tokenization API.
                  </div>
                </FormGroup>
                <FormGroup>
                  <label>Legal owner / custodian name</label>
                  <input
                    type="text"
                    placeholder="Entity or person holding title or custody"
                    value={formData.custodian}
                    onChange={(e) => handleInputChange('custodian', e.target.value)}
                  />
                  <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.35rem' }}>
                    Maps to <strong>current owner name</strong> on the asset record.
                  </div>
                </FormGroup>
              </FormSection>
            </FormGrid>
          </StepContent>
        );

      case 3:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h3 style={{ color: '#fff', marginBottom: '1.5rem' }}>
              {useAssetClassMode ? `${currentAssetClass?.name} Metadata` : `${currentCategory?.name} Specific Metadata`}
            </h3>
            
            {useAssetClassMode && currentAssetClass ? (
              /* Asset Class Metadata */
              <FormGrid>
                <FormSection>
                  <div className="section-title">
                    <FiShield />
                    Asset Class Schema ({currentAssetClass.name})
                  </div>
                  <div style={{ 
                    marginBottom: '1rem', 
                    padding: '1rem', 
                    background: 'rgba(79, 172, 254, 0.05)',
                    border: '1px solid rgba(79, 172, 254, 0.2)',
                    borderRadius: '8px',
                    color: '#a0a0a0',
                    fontSize: '0.9rem'
                  }}>
                    <strong style={{ color: '#4facfe' }}>Market:</strong> {currentAssetClass.marketSymbol}/USD • 
                    <strong style={{ color: '#4facfe' }}> Unit:</strong> {currentAssetClass.tradingUnit} •
                    <strong style={{ color: '#4facfe' }}> Type:</strong> {currentAssetClass.classType}
                  </div>
                  
                  {currentAssetClass.standardSchema?.required?.map(field => {
                    const spec = currentAssetClass.standardSchema.specifications?.[field];
                    return (
                      <FormGroup key={field}>
                        <label>
                          {field.charAt(0).toUpperCase() + field.slice(1)} *
                          {spec?.unit && <span style={{ color: '#4facfe', fontSize: '0.8rem' }}> ({spec.unit})</span>}
                        </label>
                        {spec?.enum ? (
                          <select
                            value={formData.metadata[field] || ''}
                            onChange={(e) => handleInputChange(field, e.target.value, true)}
                            style={{
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '8px',
                              padding: '0.75rem',
                              color: '#fff',
                              fontSize: '0.9rem'
                            }}
                          >
                            <option value="">Select {field}</option>
                            {spec.enum.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={
                              spec?.type === 'number' || spec?.type === 'integer' ? 'number' : 'text'
                            }
                            placeholder={`Enter ${field}${spec?.unit ? ` (${spec.unit})` : ''}`}
                            value={formData.metadata[field] ?? ''}
                            onChange={(e) => handleInputChange(field, e.target.value, true)}
                            min={spec?.min}
                            max={spec?.max}
                            step={
                              spec?.type === 'integer'
                                ? 1
                                : spec?.step !== undefined
                                  ? spec.step
                                  : spec?.type === 'number'
                                    ? 'any'
                                    : undefined
                            }
                          />
                        )}
                        {errors[`metadata.${field}`] && (
                          <div className="error">{errors[`metadata.${field}`]}</div>
                        )}
                        {spec?.min !== undefined && spec?.max !== undefined && (
                          <div style={{ fontSize: '0.8rem', color: '#a0a0a0', marginTop: '0.25rem' }}>
                            Range: {spec.min} - {spec.max} {spec.unit}
                          </div>
                        )}
                        {spec?.patternDescription && (
                          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.25rem', lineHeight: 1.45 }}>
                            {spec.patternDescription}
                          </div>
                        )}
                      </FormGroup>
                    );
                  })}
                </FormSection>

                <FormSection>
                  <div className="section-title">
                    <FiMapPin />
                    Location & jurisdiction
                  </div>
                  <FormGroup>
                    <label>Location</label>
                    <input
                      type="text"
                      placeholder="City, region (country can be set below)"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Country / jurisdiction</label>
                    <input
                      type="text"
                      placeholder="e.g. United Arab Emirates or AE"
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                    />
                  </FormGroup>
                </FormSection>
              </FormGrid>
            ) : currentCategory ? (
              /* Traditional Category Metadata */
              <FormGrid>
                <FormSection>
                  <div className="section-title">
                    <span>{currentCategory.icon}</span>
                    Category Fields
                  </div>
                  {currentCategory.metadataFields.map(field => (
                    <FormGroup key={field}>
                      <label>{field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} *</label>
                      <input
                        type="text"
                        placeholder={`Enter ${field}`}
                        value={formData.metadata[field] || ''}
                        onChange={(e) => handleInputChange(field, e.target.value, true)}
                      />
                      {errors[`metadata.${field}`] && (
                        <div className="error">{errors[`metadata.${field}`]}</div>
                      )}
                    </FormGroup>
                  ))}
                </FormSection>

                <FormSection>
                  <div className="section-title">
                    <FiMapPin />
                    Location & jurisdiction
                  </div>
                  <FormGroup>
                    <label>Location</label>
                    <input
                      type="text"
                      placeholder="City, region (country can be set below)"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Country / jurisdiction</label>
                    <input
                      type="text"
                      placeholder="e.g. United Arab Emirates or AE"
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                    />
                  </FormGroup>
                </FormSection>
              </FormGrid>
            ) : null}
          </StepContent>
        );

      case 4:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h3 style={{ color: '#fff', marginBottom: '1.5rem' }}>Upload documentation</h3>
            <FormSection>
              <div className="section-title">
                <FiUpload />
                Required documents
              </div>
              <p style={{ color: '#a0a0a0', fontSize: '0.88rem', marginBottom: '1rem', lineHeight: 1.5 }}>
                Files are sent to the API as multipart uploads (IPFS-backed). At least one file is required before
                submission. Large files may take a moment; server limit is 50 MB per upload.
              </p>
              <FileUploadArea onClick={() => document.getElementById('file-input').click()}>
                <FiUpload className="upload-icon" />
                <div className="upload-text">Click to upload documents</div>
                <div className="upload-subtext">
                  Certificates, appraisals, custody statements, or other evidence for legal review
                </div>
              </FileUploadArea>
              <input
                id="file-input"
                type="file"
                multiple
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              
              {formData.documents.length > 0 && (
                <UploadedFiles>
                  {formData.documents.map(doc => (
                    <div key={doc.id} className="file-item">
                      <FiFileText className="file-icon" />
                      <span className="file-name">{doc.name}</span>
                      <span className="file-size">{(doc.size / 1024).toFixed(1)} KB</span>
                    </div>
                  ))}
                </UploadedFiles>
              )}
              
              {errors.documents && <div style={{ color: '#ff4757', marginTop: '1rem' }}>{errors.documents}</div>}
            </FormSection>
          </StepContent>
        );

      case 5:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h3 style={{ color: '#fff', marginBottom: '1.5rem' }}>Review &amp; submit</h3>
            <FormGrid>
              <FormSection>
                <div className="section-title">
                  <FiCheck />
                  Asset summary
                </div>
                <div style={{ color: '#a0a0a0', lineHeight: 1.6 }}>
                  <p>
                    <strong>Program:</strong>{' '}
                    {useAssetClassMode
                      ? currentAssetClass?.name || '—'
                      : currentCategory?.name || '—'}
                  </p>
                  {useAssetClassMode && currentAssetClass?.sector && (
                    <p>
                      <strong>Sector (API):</strong> {formatSectorLabel(currentAssetClass.sector)}
                    </p>
                  )}
                  {!useAssetClassMode && formData.category && (
                    <p>
                      <strong>Sector (API):</strong> {formatSectorLabel(mapCategoryToSector(formData.category))}
                    </p>
                  )}
                  <p><strong>Title:</strong> {formData.name}</p>
                  <p>
                    <strong>Valuation (USD):</strong>{' '}
                    {Number(formData.valuationUSD || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    })}
                  </p>
                  <p><strong>Documents:</strong> {formData.documents.length} file(s)</p>
                  {formData.location && <p><strong>Location:</strong> {formData.location}</p>}
                  {(formData.country || formData.location) && (
                    <p>
                      <strong>Country / jurisdiction:</strong>{' '}
                      {formData.country?.trim() || 'Derived from location'}
                    </p>
                  )}
                  <p>
                    <strong>Legal owner name:</strong> {formData.custodian?.trim() || '—'}
                  </p>
                  <p>
                    <strong>Owner identifier:</strong> {formData.ownerAddress?.trim() || '—'}
                  </p>
                </div>
              </FormSection>

              <FormSection>
                <div className="section-title">
                  <FiShield />
                  Next steps
                </div>
                <div style={{ color: '#a0a0a0', lineHeight: 1.6 }}>
                  <p>
                    Submitting creates the asset via <code style={{ color: '#4facfe' }}>POST /api/assets</code>, uploads
                    documents, then calls <code style={{ color: '#4facfe' }}>submit-for-legal-review</code>.
                  </p>
                  <p>KYC profile: {kycStatus === 'verified' ? 'Verified' : 'Not verified / pending'}</p>
                  <p style={{ color: '#86efac', marginTop: '1rem' }}>
                    After submission, legal reviewers can assign and approve the case in Legal reviews.
                  </p>
                </div>
              </FormSection>
            </FormGrid>
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <WizardContainer>
      <WizardHeader>
        <WizardTitle>
          {selectedCategory?.icon} Create New Asset
        </WizardTitle>
        <CloseButton onClick={onClose}>
          <FiX />
        </CloseButton>
      </WizardHeader>

      {effectiveSector && !tokenizerCoversSector && !sectorGateActive && (
        <ReadinessNotice>
          <strong>No tokenizer for this sector.</strong> The platform requires an <strong>approved</strong> authorized
          tokenizer whose sectors include <strong>{formatSectorLabel(effectiveSector)}</strong>. Go to Authorized
          tokenizers (admin) or sign in with a tokenizer operator account, then try again.
        </ReadinessNotice>
      )}

      <ProgressBar>
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <ProgressStep
              active={currentStep === step.id}
              completed={currentStep > step.id}
            >
              <div className="step-number">
                {currentStep > step.id ? <FiCheck /> : step.id}
              </div>
              <div className="step-label">{step.label}</div>
            </ProgressStep>
            {index < steps.length - 1 && (
              <div 
                className="step-line" 
                style={{ 
                  background: currentStep > step.id ? '#00ff88' : 'rgba(255, 255, 255, 0.1)' 
                }}
              />
            )}
          </React.Fragment>
        ))}
      </ProgressBar>

      <AnimatePresence mode="wait">
        {renderStepContent()}
      </AnimatePresence>

      <WizardActions>
        <ActionButton 
          className="secondary" 
          onClick={handlePrevious}
          disabled={currentStep === 1}
        >
          <FiChevronLeft />
          Previous
        </ActionButton>

        {currentStep < 5 ? (
          <ActionButton className="primary" onClick={handleNext}>
            Next
            <FiChevronRight />
          </ActionButton>
        ) : (
          <ActionButton 
            className="primary" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Asset'}
            <FiCheck />
          </ActionButton>
        )}
      </WizardActions>
    </WizardContainer>
  );
}

export default AssetCreationWizard; 